import { makeAutoObservable } from "mobx";
import { retry } from "rxjs";
import { ApplicationSettingsModel } from "../models/ApplicationSettingsModel";
import { AccountService } from "../services";

export class ApplicationSettingsStore {
  public applicationSettings: ApplicationSettingsModel =
    new ApplicationSettingsModel();
  public isLoadingInProgress = true;
  /* True when the settings request gave up. Callers that gate access on a setting (the purchase
     domain allow-list) treat this as "unknown, do not block" rather than "not allowed", so a
     settings outage cannot turn a live purchase link into a page-not-found. */
  public loadFailed = false;
  constructor() {
    makeAutoObservable(this);
    this.loadSettings();
  }

  getFundraiserDomain(): string {
    return this.applicationSettings.fundraiserDomain;
  }
  
  getOpenAISettings() {
    return this.applicationSettings.openAISettings;
  }

  getUnsplashAccessKey(): string {
    return this.applicationSettings.unsplashAccessKey;
  }

  getGoogleMapsAPIKey(): string {
    return this.applicationSettings.googleMapsAPIKey;
  }

  /* This flag gates the whole app: RoutesConfig renders nothing but a spinner while it is true.
     The subscribe below used to pass only a success handler, so a settings request that failed
     (a dropped connection, a 5xx, a cold start) left the flag true for the life of the page and
     the app sat on that spinner forever with no way out and nothing in the UI to say why. The
     request is now retried a couple of times for the transient case, and the flag is cleared
     either way - an app running on default settings is recoverable, an endless spinner is not. */
  public loadSettings = async () => {
    this.isLoadingInProgress = true;
    AccountService.getApplicationSettings()
      .pipe(retry({ count: 2, delay: 1000 }))
      .subscribe({
        next: (response) => {
          if (response.result) {
            this.applicationSettings =
              ApplicationSettingsModel.deserializeAPIModel(response.result);
          }
          this.finishLoading(false);
        },
        error: (error) => {
          console.error("Failed to load application settings", error);
          this.finishLoading(true);
        },
      });
  };

  private finishLoading = (failed: boolean) => {
    this.loadFailed = failed;
    this.isLoadingInProgress = false;
  };

  isPurchaseDomainAllowed(domain: string): boolean {
    return (
      this.applicationSettings.purchaseDomains.some(
        (d) => d.domain?.toLocaleLowerCase() === domain?.toLocaleLowerCase()
      )
    );
  }

  isFundRaiserDomainAllowed(domain: string): boolean { 
    return (
      this.applicationSettings.fundraiserDomain?.toLocaleLowerCase() ===
      domain?.toLocaleLowerCase()
    );
  }
}

export default new ApplicationSettingsStore();
